import React, { useEffect, useState } from 'react';
import { employeeApi } from '../lib/api';
import { Input } from './ui/input';
import { Label } from './ui/label';

/* Source of Hiring and Experience — the add form, the edit form and the
   profile all render these from here rather than each spelling out its own
   dropdown. Three copies of a field list is how the add form ends up
   offering a source the edit form cannot save.

   The option lists come from the SERVER (`/api/meta/hiring`), so the
   vocabulary has exactly one definition and adding a source is a one-line
   backend change. The fallbacks below only cover the meta call failing — they
   are not a second source of truth. */

const FALLBACK = {
  sources: ['Naukri Portal', 'Employee Referral', 'LinkedIn', 'Personal Reference',
            'Walk-in', 'Recruitment Consultant/Agency', 'Other'],
  source_detail_labels: {},
  source_detail_required: ['Other'],
  experience_types: ['Fresher', 'Experienced'],
};

export function useHiringMeta() {
  const [meta, setMeta] = useState(FALLBACK);
  useEffect(() => {
    let alive = true;
    employeeApi.hiringMeta()
      .then((r) => { if (alive && r?.data) setMeta({ ...FALLBACK, ...r.data }); })
      .catch(() => {});                       // keep the fallback; never blank the form
    return () => { alive = false; };
  }, []);
  return meta;
}

const LBL = 'text-xs font-semibold text-[#5A6377] mb-1 block';
const SEL = 'border border-[#E6E9F0] rounded-md text-sm px-2 py-2 w-full bg-white';

/* Both fields are OPTIONAL on purpose. Most of the roll was entered before
   they existed, so forcing a value would mean inventing one — a blank stays a
   blank and reads as "Not Recorded" everywhere it is shown. */
export function SourceOfHiringFields({ meta, value, onChange, idPrefix = 'emp' }) {
  const m = meta || FALLBACK;
  const source = value.source_of_hiring || '';
  // What the free-text box is actually asking for changes with the source:
  // "Referred by" for a referral, "Agency name" for a consultant. A generic
  // "Detail" box gets filled with anything or nothing.
  const detailLabel = m.source_detail_labels?.[source] || 'Detail';
  const detailRequired = (m.source_detail_required || []).includes(source);

  return (
    <>
      <div>
        <Label className={LBL}>Source of Hiring</Label>
        <select data-testid={`${idPrefix}-source-of-hiring`} className={SEL} value={source}
                onChange={(e) => onChange({
                  source_of_hiring: e.target.value,
                  // Clearing the source clears its qualifier too, or a
                  // referrer's name is left attached to no source at all.
                  source_detail: e.target.value ? value.source_detail || '' : '',
                })}>
          <option value="">Not recorded</option>
          {m.sources.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <Label className={LBL}>
          {detailLabel}{detailRequired ? ' *' : ''}
        </Label>
        <Input data-testid={`${idPrefix}-source-detail`} value={value.source_detail || ''}
               disabled={!source}
               placeholder={source ? `${detailLabel}…` : 'Pick a source first'}
               onChange={(e) => onChange({ source_detail: e.target.value })} />
        {detailRequired && (
          <p className="text-[11px] text-[#98A1B2] mt-1">
            &ldquo;Other&rdquo; needs the source typed in, or it records nothing.
          </p>
        )}
      </div>
    </>
  );
}

export function ExperienceFields({ meta, value, onChange, idPrefix = 'emp' }) {
  const m = meta || FALLBACK;
  const type = value.experience_type || '';
  const fresher = type === 'Fresher';

  return (
    <>
      <div>
        <Label className={LBL}>Experience</Label>
        <select data-testid={`${idPrefix}-experience-type`} className={SEL} value={type}
                onChange={(e) => onChange({
                  experience_type: e.target.value,
                  // Fresher means zero, stated rather than left blank — a
                  // recorded 0 is a fact, an empty box is a missing answer.
                  prior_experience_years: e.target.value === 'Fresher' ? 0
                    : e.target.value === '' ? '' : value.prior_experience_years ?? '',
                })}>
          <option value="">Not recorded</option>
          {m.experience_types.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <Label className={LBL}>Previous experience (years){type === 'Experienced' ? ' *' : ''}</Label>
        <Input data-testid={`${idPrefix}-experience-years`} type="number" min="0" max="60" step="0.5"
               disabled={!type || fresher}
               value={fresher ? 0 : (value.prior_experience_years ?? '')}
               placeholder={!type ? 'Pick Fresher or Experienced' : '2.5'}
               onChange={(e) => onChange({ prior_experience_years: e.target.value })} />
        <p className="text-[11px] text-[#98A1B2] mt-1">
          {/* The single most misread thing about this field. */}
          Experience brought <b>at joining</b> — it is not recalculated as the years pass.
        </p>
      </div>
    </>
  );
}

/* What the profile shows. Kept here so "Not Recorded" is worded identically
   to the table and can never drift into "0 Years". */
export function HiringSummary({ employee }) {
  const none = <span className="text-[#C9CFDB] italic">Not Recorded</span>;
  return (
    <>
      <div>
        <p className="text-xs text-[#98A1B2] mb-0.5">Source of Hiring</p>
        <p className="text-sm text-[#11182B]" data-testid="profile-source-of-hiring">
          {employee?.source_of_hiring || none}
          {employee?.source_detail && (
            <span className="block text-xs text-[#5A6377]">{employee.source_detail}</span>
          )}
        </p>
      </div>
      <div>
        <p className="text-xs text-[#98A1B2] mb-0.5">Experience</p>
        <p className="text-sm text-[#11182B]" data-testid="profile-experience">
          {employee?.experience_type ? (employee.experience_label
            || (employee.experience_type === 'Fresher' ? 'Fresher' : 'Experienced')) : none}
        </p>
      </div>
    </>
  );
}
